import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { List, X, Phone, UserCircle } from "@phosphor-icons/react";
import { PHONE, PHONE_RAW, COMPANY_UPPER, AKA } from "../data";
import { useAuth } from "../context/AuthContext";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Executive", href: "#executive" },
  { label: "Why Us", href: "#why" },
  { label: "Corporate", href: "#corporate" },
  { label: "Events", href: "#events" },
  { label: "Areas", href: "#areas" },
  { label: "Reviews", href: "#reviews" },
  { label: "Special", route: "/special-arrangements" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (link) => {
    setOpen(false);
    if (link.route) {
      navigate(link.route);
      window.scrollTo({ top: 0 });
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.querySelector(link.href)?.scrollIntoView({ behavior: "smooth" }), 120);
    } else {
      document.querySelector(link.href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-ink/10"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
        <button
          data-testid="logo-home"
          onClick={() => { navigate("/"); window.scrollTo({ top: 0 }); }}
          className="flex items-center gap-3"
        >
          <img src="/ds-cabs-logo.png" alt="DS CABS & Delivery inc." className="h-11 sm:h-12 w-auto" />
          <span data-testid="brand-name" className="flex flex-col leading-none text-left">
            <span className="font-display font-extrabold text-ink text-[13px] sm:text-base tracking-tight whitespace-nowrap">{COMPANY_UPPER}</span>
            <span className="text-[9px] sm:text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/50">({AKA})</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-6">
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
              onClick={() => go(l)}
              className="text-sm font-semibold text-ink/70 hover:text-ink transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-brandred group-hover:w-full transition-all duration-300" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="header-account-btn"
            onClick={() => { setOpen(false); navigate("/account"); window.scrollTo({ top: 0 }); }}
            className="flex items-center gap-1.5 text-ink font-bold text-sm hover:text-brandred transition-colors"
            title={user ? "My account" : "Sign in"}
          >
            {user?.picture
              ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full border-2 border-ink" />
              : <UserCircle weight={user ? "fill" : "regular"} size={26} />}
            <span className="hidden sm:inline">{user ? (user.name?.split(" ")[0] || "Account") : "Sign in"}</span>
          </button>
          <a
            data-testid="header-call-btn"
            href={`tel:${PHONE_RAW}`}
            className="hidden sm:flex items-center gap-2 bg-sun text-ink font-bold text-sm px-4 py-2.5 border-2 border-ink shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all duration-150"
          >
            <Phone weight="fill" size={16} /> {PHONE}
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-ink p-1"
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <List size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:hidden bg-white border-b border-ink/10 px-5 py-4 flex flex-col gap-1"
        >
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(" ", "-")}`}
              onClick={() => go(l)}
              className="text-left py-3 font-semibold text-ink border-b border-ink/5"
            >
              {l.label}
            </button>
          ))}
          <a href={`tel:${PHONE_RAW}`} className="mt-3 flex items-center justify-center gap-2 bg-sun text-ink font-bold px-4 py-3 border-2 border-ink">
            <Phone weight="fill" size={18} /> Call {PHONE}
          </a>
        </motion.div>
      )}
    </motion.header>
  );
};
